import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Trash2, ExternalLink, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MyCommunityPostsPage = () => {
  const { token } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const authHeader = { Authorization: `Bearer ${token}` };

  /* eslint-disable react-hooks/exhaustive-deps, react-hooks/set-state-in-effect */
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API_URL}/api/community/me/posts`, {
          headers: authHeader,
          params: { limit: 60 },
        });
        if (alive) setPosts(r.data.items || []);
      } catch (e) {
        toast.error("Could not load your posts.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps, react-hooks/set-state-in-effect */

  const handleUnshare = async (post) => {
    if (!window.confirm("Remove this post from the community feed?")) return;
    try {
      await axios.delete(`${API_URL}/api/community/posts/${post.id}`, { headers: authHeader });
      setPosts((prev) => prev.filter((p) => p.id !== post.id));
      toast.success("Removed from community.");
    } catch (e) {
      toast.error("Could not remove.");
    }
  };

  return (
    <div className="space-y-6 px-4 lg:px-0" data-testid="community-my-posts-page">
      <div>
        <h1 className="text-4xl font-black text-ink-950 tracking-tight">My community posts</h1>
        <p className="text-ink-500 mt-2">Manage what you&apos;ve shared. Unshare anytime.</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-ink-500" />
        </div>
      ) : posts.length === 0 ? (
        <div className="rounded-3xl border-2 border-dashed border-ink-200 p-12 text-center">
          <Sparkles className="w-10 h-10 mx-auto text-ink-300 mb-3" />
          <p className="text-sm text-ink-500 mb-4">You haven&apos;t shared anything yet.</p>
          <Link to="/dashboard/community">
            <Button className="bg-ink-950 text-white">Go to community</Button>
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {posts.map((p) => (
            <div
              key={p.id}
              className="rounded-3xl overflow-hidden border border-ink-200 bg-white"
              data-testid={`my-community-post-${p.id}`}
            >
              <div className="bg-ink-100 relative">
                {p.kind === "video" ? (
                  <video
                    src={p.media_url}
                    muted
                    playsInline
                    preload="metadata"
                    className="w-full aspect-square object-cover"
                  />
                ) : (
                  <img src={p.media_url} alt="" className="w-full aspect-square object-cover" />
                )}
                {p.status === "under_review" && (
                  <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-bold uppercase tracking-wide">
                    Under review
                  </span>
                )}
              </div>
              <div className="p-4 space-y-2">
                {p.caption && <p className="text-sm text-ink-700 line-clamp-2">{p.caption}</p>}
                <div className="flex items-center gap-3 text-xs text-ink-500">
                  <span>♥ {p.likes_count || 0}</span>
                  <span>💬 {p.comments_count || 0}</span>
                </div>
                <div className="flex items-center gap-2 pt-2">
                  <Link to={`/dashboard/community`} className="flex-1">
                    <Button variant="outline" size="sm" className="w-full">
                      <ExternalLink className="w-3 h-3" />
                      View in feed
                    </Button>
                  </Link>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleUnshare(p)}
                    data-testid={`my-community-unshare-${p.id}`}
                    className="text-rose-600"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyCommunityPostsPage;
